import React, { useEffect, useMemo, useState } from "react";
import API_BASE from "../utils/apiBase";
import "./AdminCategories.css";

function AdminCountries() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [countries, setCountries] = useState([]);
  const [adding, setAdding] = useState(false);
  const [checkedRole, setCheckedRole] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newCountry, setNewCountry] = useState({
    name: "",
    code: "",
    isdCode: "",
    currencyCode: "",
    enabled: true,
  });

  // JSON headers only (no Admin Key anymore)
  const headers = useMemo(() => ({ "Content-Type": "application/json" }), []);

  // Determine role from currentUser in localStorage
  useEffect(() => {
    function getCurrentUser() {
      const s = sessionStorage.getItem("currentUser");
      if (s) {
        try {
          return JSON.parse(s);
        } catch (_e) {
          /* ignore */
        }
      }
      if (localStorage.getItem("rememberMe") === "true") {
        const l = localStorage.getItem("currentUser");
        if (l) {
          try {
            return JSON.parse(l);
          } catch (_e) {
            /* ignore */
          }
        }
      }
      return null;
    }
    function sync() {
      try {
        const cu = getCurrentUser();
        let admin = false;
        if (cu) {
          const role = String(cu.roleType || "").toLowerCase();
          const emailLower = String(cu.emailId || "").toLowerCase();
          admin =
            role === "administrator" ||
            role === "administrative" ||
            emailLower === "admin@buymyskills.local";
        }
        setIsAdmin(admin);
      } catch {
        setIsAdmin(false);
      } finally {
        setCheckedRole(true);
      }
    }
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("auth-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("auth-changed", sync);
    };
  }, []);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/countries`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Failed to load (${res.status})`);
      }
      setCountries(Array.isArray(data.countries) ? data.countries : []);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      load();
    }
    // load only when admin state flips true
  }, [isAdmin]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setErr("");
    if (!newCountry.name.trim() || !newCountry.code.trim()) {
      setErr("Name and code are required");
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/countries`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: newCountry.name.trim(),
          code: newCountry.code.trim().toUpperCase(),
          phoneCode: newCountry.isdCode.trim(),
          currencyCode: newCountry.currencyCode.trim().toUpperCase(),
          enabled: !!newCountry.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Create failed (${res.status})`);
      }
      setNewCountry({
        name: "",
        code: "",
        isdCode: "",
        currencyCode: "",
        enabled: true,
      });
      await load();
    } catch (e) {
      setErr(e.message || "Create failed");
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (id, patch) => {
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/countries/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Update failed (${res.status})`);
      }
      await load();
    } catch (e) {
      setErr(e.message || "Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this country?")) return;
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/countries/${id}`, {
        method: "DELETE",
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Delete failed (${res.status})`);
      }
      await load();
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  };

  if (!checkedRole) {
    // Wait until we know the role to avoid flicker
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="container-fluid">
        <div style={{ paddingBottom: "1.5rem" }}>
          <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0 text-gray-800">Admin: Countries</h1>
          </div>
          <div className="alert alert-warning" role="alert">
            You are not authorized to view this page.
          </div>
        </div>
      </div>
    );
  }

  const disabled = !isAdmin;

  return (
    <div className="container-fluid">
      <div style={{ paddingBottom: "1.5rem" }}>
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">Admin: Countries</h1>
        </div>

        {err ? (
          <div className="alert alert-danger" role="alert">
            {err}
          </div>
        ) : null}

        <div className="card mb-4 admin-category-card">
          <div className="card-header category-card-header">Add Country</div>
          <div className="card-body">
            <form onSubmit={handleAdd}>
              <div className="form-row">
                <div className="form-group col-md-2">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newCountry.name}
                    onChange={(e) =>
                      setNewCountry((p) => ({ ...p, name: e.target.value }))
                    }
                    disabled={disabled || adding}
                  />
                </div>
                <div className="form-group col-md-2">
                  <label>Code</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={3}
                    value={newCountry.code}
                    onChange={(e) =>
                      setNewCountry((p) => ({
                        ...p,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    disabled={disabled || adding}
                  />
                </div>
                <div className="form-group col-md-2">
                  <label>ISD Code</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={6}
                    value={newCountry.isdCode}
                    onChange={(e) =>
                      setNewCountry((p) => ({
                        ...p,
                        isdCode: e.target.value,
                      }))
                    }
                    disabled={disabled || adding}
                  />
                </div>
                <div className="form-group col-md-2">
                  <label>Currency Code</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={3}
                    value={newCountry.currencyCode}
                    onChange={(e) =>
                      setNewCountry((p) => ({
                        ...p,
                        currencyCode: e.target.value.toUpperCase(),
                      }))
                    }
                    disabled={disabled || adding}
                  />
                </div>
                <div className="form-group col-md-2">
                  <label style={{ display: "block" }}>Enabled</label>
                  <div className="form-check">
                    <input
                      id="new-country-enabled"
                      className="form-check-input"
                      type="checkbox"
                      checked={!!newCountry.enabled}
                      onChange={(e) =>
                        setNewCountry((p) => ({
                          ...p,
                          enabled: e.target.checked,
                        }))
                      }
                      disabled={disabled || adding}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="new-country-enabled"
                    >
                      Yes
                    </label>
                  </div>
                </div>
                <div className="form-group col-md-2 d-flex align-items-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={disabled || adding}
                  >
                    {adding ? "Adding..." : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="card manage-category-card">
          <div className="card-header category-card-header">
            Manage Countries
          </div>
          <div className="card-body">
            {loading ? (
              <div>Loading...</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Name</th>
                      <th style={{ width: 50 }}>Code</th>
                      <th style={{ width: 70 }}>ISD Code</th>
                      <th style={{ width: 70 }}>Currency Code</th>
                      <th style={{ width: 50 }}>Enabled</th>
                      <th style={{ width: 100 }}>Created</th>
                      <th style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          No countries
                        </td>
                      </tr>
                    ) : (
                      countries
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((c) => (
                          <tr key={c.id}>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={c.name}
                                onChange={(e) => {
                                  const name = e.target.value;
                                  setCountries((prev) =>
                                    prev.map((row) => {
                                      if (row.id === c.id) {
                                        return { ...row, name };
                                      }
                                      return row;
                                    }),
                                  );
                                }}
                                disabled={disabled}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                maxLength={3}
                                value={c.code}
                                onChange={(e) => {
                                  const code = e.target.value.toUpperCase();
                                  setCountries((prev) =>
                                    prev.map((row) => {
                                      if (row.id === c.id) {
                                        return { ...row, code };
                                      }
                                      return row;
                                    }),
                                  );
                                }}
                                disabled={disabled}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                maxLength={6}
                                value={c.isdCode || ""}
                                onChange={(e) => {
                                  const isdCode = e.target.value;
                                  setCountries((prev) =>
                                    prev.map((row) => {
                                      if (row.id === c.id) {
                                        return { ...row, isdCode };
                                      }
                                      return row;
                                    }),
                                  );
                                }}
                                disabled={disabled}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                maxLength={3}
                                value={c.currencyCode || ""}
                                onChange={(e) => {
                                  const currencyCode =
                                    e.target.value.toUpperCase();
                                  setCountries((prev) =>
                                    prev.map((row) => {
                                      if (row.id === c.id) {
                                        return { ...row, currencyCode };
                                      }
                                      return row;
                                    }),
                                  );
                                }}
                                disabled={disabled}
                              />
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={
                                  c.enabled === 1 ||
                                  c.enabled === true ||
                                  c.enabled === "Y"
                                }
                                onChange={(e) => {
                                  const enabled = e.target.checked ? 1 : 0;
                                  setCountries((prev) =>
                                    prev.map((row) => {
                                      if (row.id === c.id) {
                                        return { ...row, enabled };
                                      }
                                      return row;
                                    }),
                                  );
                                }}
                                disabled={disabled}
                              />
                            </td>
                            <td>
                              <span style={{ fontSize: 12, color: "#666" }}>
                                {c.createdAt}
                              </span>
                            </td>
                            <td>
                              <div
                                className="btn-group btn-group-sm"
                                style={{ gap: ".2rem" }}
                              >
                                <button
                                  className="btn btn-primary"
                                  disabled={disabled}
                                  onClick={() =>
                                    handleUpdate(c.id, {
                                      name: c.name,
                                      code: c.code,
                                      phoneCode: c.isdCode,
                                      currencyCode: c.currencyCode,
                                      enabled:
                                        c.enabled === 1 ||
                                        c.enabled === true ||
                                        c.enabled === "Y"
                                          ? 1
                                          : 0,
                                    })
                                  }
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-danger"
                                  disabled={disabled}
                                  onClick={() => handleDelete(c.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>
              Note: Only Enabled countries are returned by GET /api/countries
              and shown in dropdowns.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminCountries;
